import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Stack
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { get881903Program, fetch881903ProgramSummary } from '../services/cr881903';

export default function EpisodeSummary({ episode }) {
  const crProg = get881903Program(episode.showTitle || episode.name);

  // Initialize with cached summary if available in localStorage
  const [summaryData, setSummaryData] = useState(() => {
    if (!crProg) return null;
    try {
      const cached = localStorage.getItem(`cr_summary_${crProg.id}_${episode.date}`);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!summaryData && crProg) {
      let isMounted = true;
      setLoading(true);

      fetch881903ProgramSummary(crProg.id, episode.date)
        .then((res) => {
          if (isMounted) {
            setSummaryData(res);
          }
        })
        .catch(() => {
          if (isMounted) {
            setSummaryData({ success: false, sections: [] });
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });

      return () => {
        isMounted = false;
      };
    }
  }, [crProg, episode.date, summaryData]);

  // If loading Commercial Radio summary
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          載入商台每半小時節目摘要...
        </Typography>
      </Box>
    );
  }

  // If Commercial Radio summary was found with half-hour sections
  if (summaryData && summaryData.sections && summaryData.sections.length > 0) {
    return (
      <Stack
        spacing={0.75}
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8f9fa'),
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {summaryData.sections.map((sec, sIdx) => (
          <Box
            key={sIdx}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1
            }}
          >
            <Chip
              icon={<ScheduleIcon sx={{ fontSize: '13px !important' }} />}
              label={sec.time}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: '0.72rem',
                fontWeight: 600,
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff')
              }}
            />
            <Typography variant="body2" fontWeight={sec.title ? 500 : 400}>
              {sec.title || (episode.topic && sIdx === 0 ? episode.topic : (
                <span style={{ opacity: 0.6 }}>(此時段未特別註明主題)</span>
              ))}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  // Fallback: If no 881903 summary or not a CR show, display the episode's topic
  if (episode.topic) {
    return (
      <Typography variant="body2" color="text.primary">
        {episode.topic}
      </Typography>
    );
  }

  return null;
}
